/**
 * Avatar utilities for Frantic Fanfic
 * Centralized avatar data and validation logic
 */

interface AvatarDefinition {
  id: number;
  premium: boolean;
  konami?: boolean;
}

/**
 * Complete list of all available avatars
 * Premium avatars require user to have premium status
 * Konami avatars require special code activation
 */
export const avatars: AvatarDefinition[] = [
  { id: 0, premium: false },
  { id: 24, premium: true },
  { id: 1, premium: false },
  { id: 2, premium: false },
  { id: 3, premium: false },
  { id: 4, premium: false },
  { id: 5, premium: false },
  { id: 6, premium: false },
  { id: 7, premium: false },
  { id: 8, premium: false },
  { id: 9, premium: false },
  { id: 25, premium: true },
  { id: 10, premium: false },
  { id: 26, premium: true },
  { id: 11, premium: false },
  { id: 12, premium: false },
  { id: 13, premium: false },
  { id: 27, premium: true },
  { id: 14, premium: false },
  { id: 15, premium: false },
  { id: 16, premium: false },
  { id: 28, premium: true },
  { id: 17, premium: false },
  { id: 29, premium: true },
  { id: 18, premium: false },
  { id: 19, premium: false },
  { id: 30, premium: true },
  { id: 20, premium: false },
  { id: 21, premium: false },
  { id: 22, premium: false },
  { id: 23, premium: true, konami: true },
];

/**
 * Get avatars filtered by user's premium status and konami code
 * @param premium - Whether user has premium status
 * @param konami - Whether konami code is active
 * @returns Filtered list of available avatars
 */
export function getAvatars(premium?: boolean, konami?: boolean): AvatarDefinition[] {
  return avatars
    // Include avatar if: not premium, OR user is premium, OR (it's a konami avatar AND konami code is active)
    .filter((a) => !a.premium || premium || (a.konami && konami))
    // Konami avatars additionally need: konami code OR premium
    .filter((a) => !a.konami || konami || premium);
}

/**
 * Check if a specific avatar is valid for the current user
 * @param spriteId - Avatar sprite ID to validate
 * @param userPremium - Whether user has premium status
 * @param konami - Whether konami code is active
 * @returns True if avatar is available to user, false otherwise
 */
export function isAvatarValidForUser(
  spriteId: number,
  userPremium: boolean,
  konami?: boolean
): boolean {
  const avatar = avatars.find((a) => a.id === spriteId);
  if (!avatar) return false;

  // Konami avatars are accessible with konami code OR premium
  if (avatar.konami) {
    return konami || userPremium;
  }

  // Non-premium users can't use premium avatars
  if (avatar.premium && !userPremium) return false;

  return true;
}

/**
 * Get a valid sprite ID for the user, falling back to default if invalid
 * @param spriteId - Desired sprite ID
 * @param userPremium - Whether user has premium status
 * @param konami - Whether konami code is active
 * @returns Valid sprite ID (falls back to first available if invalid)
 */
export function getValidSpriteId(
  spriteId: number,
  userPremium: boolean,
  konami?: boolean
): number {
  // Check if requested sprite is valid for current user
  if (isAvatarValidForUser(spriteId, userPremium, konami)) {
    return spriteId;
  }

  // Fallback to first available avatar
  const availableAvatars = getAvatars(userPremium, konami);
  return availableAvatars[0]?.id ?? 0;
}

/**
 * Default avatar ID (Ghost)
 */
export const DEFAULT_AVATAR_ID = 0;
